import ListoryStore from '@/store';

const MAXIMUM_AGE = 1000 * 60 * 30;
const TIMEOUT = 1000 * 60 * 10;

export default class GeolocatorService {
    public watchId: number;
    public positionsReceived = 0;
    public lastError: PositionError = null;
    public lastPosition: Position = null;

    public geoOptions = {
        enableHighAccuracy: true,
        maximumAge: MAXIMUM_AGE,
        timeout: TIMEOUT
    };

    private store: ListoryStore;

    public constructor(store: ListoryStore) {
        this.store = store;
    }

    public requestHighAccuracyLocation(
        onGeoSuccessCallback: PositionCallback,
        onGeoErrorCallback: PositionErrorCallback
    ) {
        // Do not reuse positions, keep timeout short
        const singlePositionOptions = {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 3000
        };

        navigator.geolocation.getCurrentPosition(
            pos => {
                this.geoSuccess(pos);
                onGeoSuccessCallback(pos);
            },
            err => {
                this.geoError(err);
                onGeoErrorCallback(err);
            },
            singlePositionOptions
        );
    }

    public start() {
        // Update high accuracy setting on start;
        this.geoOptions.enableHighAccuracy = this.store.settings.highAccuracyLocation;

        this.watchId = navigator.geolocation.watchPosition(
            pos => this.geoSuccess(pos),
            err => this.geoError(err),
            this.geoOptions
        );
    }

    public stop() {
        if (this.watchId) {
            navigator.geolocation.clearWatch(this.watchId);
        }
    }

    public restart() {
        this.stop();

        setTimeout(() => {
            this.start();
        }, 200);
    }

    public async checkAvailability(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            try {
                if (!('geolocation' in navigator)) {
                    resolve(false);
                }

                navigator.geolocation.getCurrentPosition(
                    position => {
                        this.store.$emit('positionUpdated', position);
                        resolve(true);
                    },
                    error => {
                        resolve(error.code !== error.PERMISSION_DENIED);
                    },
                    { timeout: 5000 }
                );
            } catch (err) {
                reject();
            }
        });
    }

    public geoSuccess(position: Position) {
        this.positionsReceived++;
        this.lastPosition = position;

        this.store.$emit('positionUpdated', position);
    }

    public geoError(error: PositionError) {
        this.lastError = error;
        this.store.$emit('positionError', error);
    }
}
